import { Dropdown, Menu, Tooltip } from 'antd'
import React, { useEffect, useRef } from 'react'
import { withNamespaces } from 'react-i18next'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { magnifierSizes } from '../constants/assessmentPlayer'

const MagnifierDropDown = ({
  handleMagnifier = () => {},
  magnifierSize = magnifierSizes.SMALL,
  children,
  tooltipPlacement = '',
  t: i18Translate,
  zIndex = 1100,
  disabled = false,
  setIsMagnifierDropdownVisible = () => {},
  setIsKeyboardFocussed = () => {},
  isMagnifierDropdownVisible = false,
  isKeybordFocussed = false,
}) => {
  const isDropdownOpen = isMagnifierDropdownVisible || isKeybordFocussed
  const menuRef = useRef(null)
  const menuItemRefs = useRef([])

  const handleMenuClick = (e) => {
    setIsKeyboardFocussed(false)
    setIsMagnifierDropdownVisible(false)
    if (magnifierSize === e.key) {
      handleMagnifier('')
    } else {
      handleMagnifier(e.key)
    }
  }

  const handleMenuArrowNavigation = (key) => {
    const menuItems = menuItemRefs.current.filter((ref) => ref !== null)
    if (menuItems.length === 0) return

    const currentIndex = menuItems.findIndex(
      (item) => item === document.activeElement
    )

    let nextIndex
    if (key === 'ArrowDown') {
      nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % menuItems.length
    } else if (key === 'ArrowUp') {
      nextIndex = currentIndex <= 0 ? menuItems.length - 1 : currentIndex - 1
    }

    if (nextIndex >= 0 && menuItems[nextIndex]) {
      menuItems[nextIndex].focus()
    }
  }

  const handleButtonKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (isDropdownOpen) {
        setIsKeyboardFocussed(false)
        setIsMagnifierDropdownVisible(false)
      } else {
        setIsKeyboardFocussed(true)
        setIsMagnifierDropdownVisible(true)
        // Focus first menu item when opening with keyboard
        setTimeout(() => {
          const firstMenuItem = menuItemRefs.current[0]
          if (firstMenuItem) {
            firstMenuItem.focus()
          }
        }, 0)
      }
    } else if (
      isDropdownOpen &&
      (e.key === 'ArrowDown' || e.key === 'ArrowUp')
    ) {
      // Handle arrow keys when dropdown is open
      e.preventDefault()
      e.stopPropagation()
      handleMenuArrowNavigation(e.key)
    }
  }

  const handleMenuItemKeyDown = (e, menuKey) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      e.stopPropagation()
      handleMenuArrowNavigation('ArrowDown')
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      e.stopPropagation()
      handleMenuArrowNavigation('ArrowUp')
    } else if (e.key === 'Home') {
      e.preventDefault()
      e.stopPropagation()
      const firstMenuItem = menuItemRefs.current[0]
      if (firstMenuItem) {
        firstMenuItem.focus()
      }
    } else if (e.key === 'End') {
      e.preventDefault()
      e.stopPropagation()
      const lastMenuItem = menuItemRefs.current[menuItemRefs.current.length - 1]
      if (lastMenuItem) {
        lastMenuItem.focus()
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      handleMenuClick({ key: menuKey })
    } else if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      setIsKeyboardFocussed(false)
      setIsMagnifierDropdownVisible(false)
      // Return focus to button
      if (children && children.props && typeof children.props.id === 'string') {
        const button = document.getElementById(children.props.id)
        if (button) {
          button.focus()
        }
      }
    }
  }

  useEffect(() => {
    const handleEscapeKeyPress = (e) => {
      if (e.key === 'Escape' && isDropdownOpen) {
        setIsKeyboardFocussed(false)
        setIsMagnifierDropdownVisible(false)
        // Return focus to button
        if (
          children &&
          children.props &&
          typeof children.props.id === 'string'
        ) {
          const button = document.getElementById(children.props.id)
          if (button) {
            button.focus()
          }
        }
      }
    }
    document.addEventListener('keydown', handleEscapeKeyPress)

    return () => {
      document.removeEventListener('keydown', handleEscapeKeyPress)
    }
  }, [isDropdownOpen, children])

  // Update menu item refs when dropdown opens
  useEffect(() => {
    if (isDropdownOpen) {
      const updateMenuItemRefs = () => {
        // Find the dropdown overlay in the DOM
        const dropdownOverlay = document.querySelector(
          '.ant-dropdown:not(.ant-dropdown-hidden)'
        )
        if (dropdownOverlay && dropdownOverlay.querySelectorAll) {
          const items = dropdownOverlay.querySelectorAll('[role="menuitem"]')
          menuItemRefs.current = Array.from(items)
          // Make items focusable
          menuItemRefs.current.forEach((item) => {
            if (item && item.setAttribute) {
              item.setAttribute('tabIndex', '0')
            }
          })
        }
      }
      // Wait for menu to render
      setTimeout(updateMenuItemRefs, 50)
    } else {
      // Clear refs when dropdown closes
      menuItemRefs.current = []
    }
  }, [isDropdownOpen])

  // Focus first menu item when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && isKeybordFocussed) {
      setTimeout(() => {
        const firstMenuItem = menuItemRefs.current[0]
        if (firstMenuItem) {
          firstMenuItem.focus()
        }
      }, 100)
    }
  }, [isDropdownOpen, isKeybordFocussed])

  const menu = (
    <Menu
      ref={menuRef}
      onClick={handleMenuClick}
      selectedKeys={[magnifierSize]}
      role="menu"
    >
      <Menu.Item
        key={magnifierSizes.SMALL}
        role="menuitem"
        tabIndex={-1}
        onKeyDown={(e) => handleMenuItemKeyDown(e, magnifierSizes.SMALL)}
        aria-label={`${i18Translate(
          'header:toolbar.magnifySmall'
        )} Size Magnifier`}
      >
        {i18Translate('header:toolbar.magnifySmall')}
      </Menu.Item>
      <Menu.Item
        key={magnifierSizes.MEDIUM}
        role="menuitem"
        tabIndex={-1}
        onKeyDown={(e) => handleMenuItemKeyDown(e, magnifierSizes.MEDIUM)}
        aria-label={`${i18Translate(
          'header:toolbar.magnifyMedium'
        )} Size Magnifier`}
      >
        {i18Translate('header:toolbar.magnifyMedium')}
      </Menu.Item>
    </Menu>
  )

  return (
    <Tooltip
      placement={tooltipPlacement}
      title={tooltipPlacement ? i18Translate('header:toolbar.magnify') : ''}
    >
      <Dropdown
        overlayStyle={{
          zIndex,
        }}
        overlay={menu}
        placement="bottomLeft"
        trigger={['click']}
        disabled={disabled}
        visible={isDropdownOpen}
        onVisibleChange={(v) => {
          if (!disabled) {
            setIsMagnifierDropdownVisible(v)
            if (!v) {
              setIsKeyboardFocussed(false)
            }
          }
        }}
      >
        {React.cloneElement(children, {
          'aria-expanded': isDropdownOpen,
          onKeyDown: (e) => {
            handleButtonKeyDown(e)
            if (children.props.onKeyDown) {
              children.props.onKeyDown(e)
            }
          },
          onClick: (e) => {
            if (!disabled) {
              if (isDropdownOpen) {
                setIsKeyboardFocussed(false)
                setIsMagnifierDropdownVisible(false)
              } else {
                setIsMagnifierDropdownVisible(true)
              }
            }
            if (children.props.onClick) {
              children.props.onClick(e)
            }
          },
          onFocus: () => {
            // Don't open dropdown on focus - only on click/Enter/Space
            // This prevents focus trap for keyboard users
            // Explicitly ensure isKeyboardFocussed is false on focus
            setIsKeyboardFocussed(false)
            // Don't call original onFocus to prevent it from setting isKeyboardFocussed to true
            // This ensures keyboard users can tab through without the dropdown opening
          },
        })}
      </Dropdown>
    </Tooltip>
  )
}

const enhance = compose(
  withNamespaces('header'),
  connect((state) => ({
    magnifierSize: state.testPlayer?.magnifierSize,
  }))
)

export default enhance(MagnifierDropDown)
